// HealthAssessments.jsx
// Route pattern : /:assessmentType-assessment  (e.g. /mental-assessment)
// Welcome route : /health-assessment
// On unknown type → redirect to /health-assessment
// On refresh     → restores progress from localStorage

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import styles from '../styles/assessments.module.css';
import cardstyles from '../styles/pagestyles.module.css';

// 🔴 REMOVED — no longer importing the static JSON directly.
// import lifeStageData from '../components/common/LifeStagesConditions.json';

// ✅ ADDED — same file WelcomeScreen.jsx already imports from, so both
// components share one fetch/cache. Adjust the path if your folder
// structure differs — this assumes GetConditons.js lives in
// src/components/HealthAssessment/.
import { getAllLifeStages } from '../components/HealthAssessment/GetConditons';

import WelcomeScreen from '../components/HealthAssessment/WelcomeScreen';
import GenericScreen from '../components/HealthAssessment/GenericScreen';
import MenopauseCareIntent from '../components/HealthAssessment/MenopauseCareIntent';
import ReviewSubmitScreen from '../components/HealthAssessment/ReviewSubmitScreen';
import ResultsScreen from '../components/HealthAssessment/ResultsScreen';
import ComingSoonScreen from '../components/HealthAssessment/ComingSoonScreen';
import { computeScore } from '../utils/scoreUtils';
import LifeStageGuide from '../components/common/LifeStagesCard';

import { StartCareAssessmentScreen } from './HealthAssessmentJourney';
import NewPatientScreen from '../components/CareJourney/Newpatientscreen';
import ProfileScreen from '../components/CareJourney/Profilescreen';
import HeightWeightScreen from '../components/CareJourney/Heightweightscreen';
import ViewResults from '../components/HealthAssessment/ViewSummary';

import CONFIG from '../config/carenavigator.json';
import ConsentScreen from '../components/Reusablecomponents/Consentscreen/Consentscreen';
import DigitalHealthFAB from '../components/Digitalhealth';
import config from './HealthAssessment.json';
import { BASE_URL } from '../api/apiClient';
import { org_text } from '../config';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const WELCOME_PATH = '/health-assessment';
const STORAGE_KEY = 'ha_progress_v1';

const normalizeSlug = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/%20/g, '-')
    .replace(/[\s/]+/g, '-')
    .replace(/[^a-z0-9-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '');

// ─────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────

const saveProgress = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) { }
};
const loadProgress = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
};
const clearProgress = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { }
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const HealthAssessments = () => {
  const { assessmentType: routeParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 'start' is the true entry point — pre-screens run before the WelcomeScreen picker
  const [screen, setScreen] = useState('start');
  const [assessmentType, setAssessmentType] = useState(null);
  const [allAnswers, setAllAnswers] = useState({});
  const [preData, setPreData] = useState({});
  const [openLastQuestion, setOpenLastQuestion] = useState(false);

  const [patientState, setPatientState] = useState({
    name: '',
    mobile: '',
    dob: '',
    ageGroup: '',
    occupation: '',
    marital: '',
    heightIn: '',
    weightIn: '',
    otp: ['', '', '', '', '', ''],
    otpSent: false,
    otpVerified: false,
    nameError: '',
    phoneError: '',
  });

  const mergePatientState = (d) => setPatientState(prev => ({ ...prev, ...d }));

  const mergePreData = useCallback(
    (d) => setPreData((prev) => ({ ...prev, ...d })),
    [],
  );

  // ── Life stages data — fetched once from the API ────────
  // These used to be plain module-level consts built from a static JSON
  // import. Now they live in state, because they arrive asynchronously.
  const [lifeStages, setLifeStages] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadLifeStages() {
      setGroupsLoading(true);
      setGroupsError(null);
      try {
        const stages = await getAllLifeStages();
        if (!cancelled) setLifeStages(stages);
      } catch (e) {
        if (!cancelled) setGroupsError(e.message);
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    }

    loadLifeStages();
    return () => { cancelled = true; };
  }, []);

  // Flatten every stage's condition groups into one list, same shape as
  // the old module-level GROUPS constant.
  const GROUPS = useMemo(() => {
    if (!lifeStages) return [];
    return lifeStages.flatMap((stage) =>
      (stage.conditionGroups || []).map((group) => ({
        ...group,
        stageId: stage.id,
        stageLabel: stage.label,
        stageAgeRange: stage.ageRange,
      })),
    );
  }, [lifeStages]);

  // Lookup map so a route slug (or group id/name) resolves straight to its
  // group object — same shape as the old module-level GROUP_BY_KEY.
  const GROUP_BY_KEY = useMemo(() => {
    const map = new Map();
    GROUPS.forEach((group) => {
      const addKey = (key) => {
        if (key != null) map.set(String(key).toLowerCase(), group);
      };
      addKey(group.id);
      addKey(group.group);
      addKey(normalizeSlug(group.id));
      addKey(normalizeSlug(group.group));
    });
    return map;
  }, [GROUPS]);

  // These were plain functions before; now they close over GROUP_BY_KEY,
  // so they become useCallback so their identity only changes when the
  // underlying data changes (keeps the route-matching effect's deps stable).
  const getGroup = useCallback(
    (key) => {
      if (!key) return null;
      return GROUP_BY_KEY.get(String(key).toLowerCase()) || null;
    },
    [GROUP_BY_KEY],
  );

  const hasScreens = useCallback((id) => !!getGroup(id), [getGroup]);
  const getTotalSteps = useCallback((id) => (getGroup(id) ? 1 : 0), [getGroup]);

  const getScreenConf = useCallback(
    (id, step) => {
      const group = getGroup(id);
      if (!group || step !== 1) return null;
      return {
        id: 1,
        title: group.group,
        subtitle: group.conditions?.join(', '),
        questions: group.questions || [], // ← this is what reaches GenericScreen
      };
    },
    [getGroup],
  );

  // ── Route → state ──────────────────────────────────────
  useEffect(() => {
    // Wait until the life stages have loaded — otherwise every assessment
    // type looks "unknown" and we'd redirect to WELCOME_PATH by mistake.
    if (groupsLoading) return;

    const locations = location.state || {};

    if (!routeParam) {
      const saved = loadProgress();
      if (saved && !saved.assessmentType && saved.screen) {
        setPreData(saved.preData || {});
        return;
      }
      setScreen('start');
      setAssessmentType(null);
      setAllAnswers({});
      setPreData({});
      clearProgress();
      return;
    }

    const rawId = decodeURIComponent(routeParam.replace(/-assessment$/, ''));
    const id = normalizeSlug(rawId);

    const group = getGroup(id);
    if (!group) {
      clearProgress();
      navigate(WELCOME_PATH, { replace: true });
      return;
    }

    const type = group.group;

    const saved = loadProgress();
    if (saved && saved.assessmentType === type && saved.screen) {
      setAssessmentType(type);
      setAllAnswers(saved.allAnswers || {});
      setPreData(saved.preData || {});
      return;
    }

    setAssessmentType(type);
    setAllAnswers({});
    setScreen(hasScreens(type) ? 1 : 'comingSoon');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParam, groupsLoading, getGroup, hasScreens]);

  // ── Persist progress ────────────────────────────────────
  useEffect(() => {
    saveProgress({ assessmentType, screen, allAnswers, preData });
  }, [assessmentType, screen, allAnswers, preData]);

  // ── Derived ─────────────────────────────────────────────
  const totalSteps = assessmentType ? getTotalSteps(assessmentType) : 0;

  // ── Answer helpers ──────────────────────────────────────
  const updateAnswer = useCallback((screenTitle, field, value) => {
    setAllAnswers((prev) => ({
      ...prev,
      [screenTitle]: { ...(prev[screenTitle] || {}), [field]: value },
    }));
  }, []);

  const makeChangeHandler = (screenTitle) => (field, value) =>
    updateAnswer(screenTitle, field, value);

  const getData = (screenId) => allAnswers[screenId] || {};

  // ── Navigation ──────────────────────────────────────────
  const goTo = (s, options = {}) => {
    setOpenLastQuestion(!!options.openLastQuestion);
    setScreen(s);
    window.scrollTo(0, 0);
  };

  const handleStart = (id) => {
    const slug = normalizeSlug(id);
    clearProgress();
    setAssessmentType(id);
    setAllAnswers({});
    navigate(`/${slug}-assessment`);
    setScreen(hasScreens(id) ? 1 : 'comingSoon');
  };

  const handleExit = () => {
    clearProgress();
    setAssessmentType(null);
    setAllAnswers({});
    setPreData({});
    navigate(WELCOME_PATH);
    setScreen('start');
  };

  const handleRestart = handleExit;

  const handleGoBackFromFirstStep = () => {
    clearProgress();
    setAssessmentType(null);
    setAllAnswers({});
    navigate(WELCOME_PATH);
    goTo('welcome');
  };

  // ── Special Menopause last step ─────────────────────────
  const isMenopauseCareIntent = assessmentType === 'menopause' && screen === 11;

  // ── Numbered step renderer ──────────────────────────────
  const renderStep = () => {
    if (typeof screen !== 'number') return null;

    const screenConf = getScreenConf(assessmentType, screen);
    if (!screenConf) return null;

    const isLast = screen === totalSteps;
    const handleGoNext = isLast ? () => goTo('review') : () => goTo(screen + 1);
    const handleGoBack =
      screen === 1
        ? handleGoBackFromFirstStep
        : () => goTo(screen - 1, { openLastQuestion: true });

    if (isMenopauseCareIntent) {
      return (
        <>
          <DigitalHealthFAB />
          <MenopauseCareIntent
            data={getData(screenConf.title)}
            onChange={makeChangeHandler(screenConf.title)}
            onSubmit={() => goTo('review')}
            onBack={() => goTo(screen - 1)}
            onExit={handleExit}
          />
        </>
      );
    }

    return (
      <>
        <DigitalHealthFAB />
        <GenericScreen
          screenConfig={screenConf}
          data={getData(screenConf.title)}
          onChange={makeChangeHandler(screenConf.title)}
          onNext={handleGoNext}
          onBack={handleGoBack}
          onExit={handleExit}
          step={screen}
          totalSteps={totalSteps}
          openLastQuestion={openLastQuestion}
          nextLabel={isLast ? 'Review & Submit' : 'Next'}
        />
      </>
    );
  };

  const showSidebar = !['start', 'consent', 'patient', 'profile', 'bmi', 'viewSummary'].includes(screen);

  const [screenConfig, setScreenConfig] = useState(config);
  const C = CONFIG.screens.consent;

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/femcare/care-journey/?clinic=${org_text}`
        );
        const data = await res.json();
        setScreenConfig(data.screens.consent);
      } catch (err) {
        console.error(err);
      }
    };
    fetchContent();
  }, []);

  // ── Render ──────────────────────────────────────────────
  return (
    <>
      <DigitalHealthFAB />
      <div className={showSidebar ? styles.app : ''}>
        <div className={showSidebar ? styles.cardPC : ''}>
          <div className={showSidebar ? styles.card : ''}>

            {/* ── Pre-screens (run once before assessment picker) ── */}

            {screen === 'start' && (
              <StartCareAssessmentScreen
                config={config}
                onNext={(selectedJourney) => {
                  mergePreData({ selectedJourney });
                  goTo('consent');
                }}
              />
            )}

            {screen === 'consent' && (
              <div className={cardstyles.page}>
                <div className={cardstyles.card}>
                  <ConsentScreen
                    config={screenConfig}
                    onNext={() => goTo('patient')}
                    onBack={() => goTo('start')}
                  />
                </div>
              </div>
            )}

            {screen === 'patient' && (
              <div className={cardstyles.page}>
                <div className={cardstyles.card}>
                  <NewPatientScreen
                    patientState={patientState}
                    onPatientStateChange={mergePatientState}
                    onNext={() => goTo('profile')}
                    onBack={() => goTo('consent')}
                  />
                </div>
              </div>
            )}

            {screen === 'profile' && (
              <div className={cardstyles.page}>
                <div className={cardstyles.card}>
                  <ProfileScreen
                    patientState={patientState}
                    onPatientStateChange={mergePatientState}
                    onNext={(d) => {
                      mergePreData({ profile: { ...preData.profile, ...d } });
                      goTo('bmi');
                    }}
                    onBack={() => goTo('patient')}
                  />
                </div>
              </div>
            )}

            {screen === 'bmi' && (
              <div className={cardstyles.page}>
                <div className={cardstyles.card}>
                  <HeightWeightScreen
                    patientState={patientState}
                    onPatientStateChange={mergePatientState}
                    onNext={(d) => {
                      mergePreData({ bmi: { ...preData.bmi, ...d } });
                      goTo('welcome');
                    }}
                    onBack={() => goTo('profile')}
                  />
                </div>
              </div>
            )}

            {/* ── Assessment picker ──
                Waits for the life-stages fetch so WelcomeScreen never
                renders with an empty/undefined condition list. */}

            {screen === 'welcome' && (
              groupsLoading ? (
                <div style={{ padding: 32, textAlign: 'center' }}>Loading assessments…</div>
              ) : groupsError ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#d32f2f' }}>
                  Couldn't load assessments: {groupsError}
                </div>
              ) : (
                <>
                  <DigitalHealthFAB />
                  <WelcomeScreen onStart={handleStart} onBack={() => goTo('bmi')} />
                </>
              )
            )}

            {/* ── Coming soon placeholder ── */}

            {screen === 'comingSoon' && (
              <ComingSoonScreen
                assessmentType={assessmentType}
                onBack={handleRestart}
              />
            )}

            {/* ── Numbered question screens ──
                Same loading/error guard as above, since renderStep()
                depends on GROUP_BY_KEY being populated. */}

            {typeof screen === 'number' && (
              groupsLoading ? (
                <div style={{ padding: 32, textAlign: 'center' }}>Loading assessment…</div>
              ) : groupsError ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#d32f2f' }}>
                  Couldn't load assessment: {groupsError}
                </div>
              ) : (
                renderStep()
              )
            )}

            {/* ── Review & submit ── */}

            {screen === 'review' && (
              <ReviewSubmitScreen
                assessmentType={assessmentType}
                allAnswers={allAnswers}
                onBack={() => goTo(totalSteps, { openLastQuestion: true })}
                onEdit={(s) => goTo(s, { openLastQuestion: true })}
                onSubmit={() => goTo('results')}
                onExit={handleRestart}
              />
            )}

            {/* ── Results ── */}

            {screen === 'results' && (
              <ResultsScreen
                answers={allAnswers}
                assessmentType={assessmentType}
                scoreResult={computeScore(assessmentType, allAnswers)}
                onBack={() => goTo('review')}
                onRestart={handleRestart}
                goTo={() => goTo('viewSummary')}
              />
            )}

            {screen === 'viewSummary' && (
              <ViewResults
                answers={allAnswers}
                assessmentType={assessmentType}
                onBack={() => goTo('results')}
              />
            )}
          </div>
        </div>

        {!['start', 'consent', 'patient', 'profile', 'bmi', 'viewSummary'].includes(screen) && (
          <LifeStageGuide />
        )}
      </div>
    </>
  );
};

export default HealthAssessments;